import React, { useState, useEffect } from 'react';
import { X, Bell } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { getUnreadMessages, markMessageRead } from '../lib/supabase';
import { Message } from '../types';

export function MessagePopup() {
  const { user } = useAuth();
  const [unread, setUnread] = useState<Message[]>([]);
  const [current, setCurrent] = useState<Message | null>(null);

  useEffect(() => {
    async function load() {
      if (!user?.id) return;
      const messages = await getUnreadMessages(user.id);
      if (messages.length > 0) {
        setUnread(messages);
        setCurrent(messages[0]);
      }
    }
    load();
  }, [user?.id]);

  const handleDismiss = async () => {
    if (!user?.id || !current) return;
    await markMessageRead(user.id, current.id);

    const remaining = unread.filter(m => m.id !== current.id);
    setUnread(remaining);

    if (remaining.length > 0) {
      setCurrent(remaining[0]);
    } else {
      setCurrent(null);
    }
  };

  if (!current) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
    }}>
      <div style={{
        background: '#131842',
        borderRadius: '20px',
        maxWidth: '440px',
        width: '100%',
        maxHeight: '80vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid rgba(255, 213, 79, 0.2)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Bell size={20} style={{ color: '#ffd54f' }} />
            <span style={{ color: '#ffd54f', fontSize: '16px', fontWeight: 700 }}>
              Neues!
            </span>
            {unread.length > 1 && (
              <span style={{
                background: 'rgba(239, 83, 80, 0.2)',
                color: '#ef5350',
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '12px',
                fontWeight: 600,
              }}>
                +{unread.length - 1}
              </span>
            )}
          </div>
          <button
            onClick={handleDismiss}
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(255,255,255,0.4)',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content - scrollable */}
        <div style={{
          padding: '20px',
          overflowY: 'auto',
          flex: 1,
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '16px',
          }}>
            <span style={{ fontSize: '32px' }}>{current.emoji}</span>
            <h2 style={{
              color: '#e8eaf6',
              fontSize: '18px',
              fontWeight: 700,
              margin: 0,
            }}>
              {current.title}
            </h2>
          </div>

          <div style={{
            color: '#e8eaf6',
            fontSize: '14px',
            lineHeight: 1.7,
          }}
            dangerouslySetInnerHTML={{
              __html: current.content
                .replace(/\n/g, '<br/>')
                .replace(/\*\*([^*]+)\*\*/g, '<strong style="color: #ffd54f">$1</strong>')
                .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color: #ffd54f; text-decoration: underline; font-weight: 600">$1</a>')
            }}
          />
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 20px',
          borderTop: '1px solid rgba(255,255,255,0.1)',
        }}>
          <button
            onClick={handleDismiss}
            style={{
              width: '100%',
              padding: '14px',
              background: '#ffd54f',
              border: 'none',
              borderRadius: '12px',
              color: '#1a1a2e',
              fontSize: '15px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {unread.length > 1 ? 'Gelesen — naechste Nachricht' : 'Gelesen!'}
          </button>
        </div>
      </div>
    </div>
  );
}
